import React, { useEffect, useState } from "react";
import { Empty, Switch, Divider, Button, Tooltip } from "antd";
import {
  UserOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { notify } from "../../Customs/NotifyProvider";
import { ENDPOINTS } from "../../../Services/ENDPOINT";
import { GeneralService } from "../../../Services/GeneralService";
import { RoomServices } from "../../../Services/RoomServices";


const STATUS_CONFIG = {
  available: {
    accent: "#216b8dff",
    badgeBg: "#5498c9ff",
    badgeColor: "#000000ff",
    label: "Available",
  },
 
};

const DEFAULT_CONFIG = {
  accent: "#216b8dff",
  badgeBg: "#f0f0f0",
  badgeColor: "#000000ff",
  label: "Unknown",
};

const CARD_STYLES = `
  .room-card {
    background: #e2ecea90;
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    position: relative;
    border: 1px solid rgba(0,0,0,0.12);
    outline: 2px solid transparent;
    outline-offset: 0px;
    box-shadow: none;
    transition: outline-color 0.15s, box-shadow 0.15s;
  }

  .room-card:hover {
    outline-color: var(--accent-color);
    box-shadow: 0 4px 12px rgba(0,0,0,0.10);
  }

  .room-card.bulk-selected {
    outline-color: #1677ff;
    box-shadow: 0 0 0 3px rgba(22,119,255,0.2);
  }
`;

const COLS = 6;

const HistoryRoomList = ({
  selectedDate,
  getAllRooms,
  dataSource = [],
  selectedRow,
  onRowSelect,
  upcomingMap = {},
  bulkMode = false,
  selectedRooms = [],
  onBulkSelect,
  onRoomStatusChange,
}) => {
  const location = useLocation();
  const data = location.state || {};
  const navigate = useNavigate();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 60000); // update every minute

    return () => clearInterval(interval);
  }, []);

  const [loadingRoomId, setLoadingRoomId] = useState(null);
  const [check_time, setCheck_times] = useState({});

  const getCheckTime = async () => {
    try {

      const req = await RoomServices.getAllByTable('utills')

      const defaultData = {
        checkin: dayjs(req.data[0].checkin_time, 'hh:mm A'),
        checkout: dayjs(req.data[0].checkout_time, 'hh:mm A'),
      }

      setCheck_times(defaultData)

    } catch (error) {
      console.error(error);

    }
  }

  useEffect(() => {
    getCheckTime();
  }, [])

  if (!dataSource.length) {
    return <Empty description="No rooms found" style={{ padding: "40px 0" }} />;
  }

  const grouped = [...dataSource]
    .sort((a, b) => Number(a.sort_order) - Number(b.sort_order))
    .reduce((acc, room) => {
      const key = room.floor || "Unnamed";
      if (!acc[key]) acc[key] = [];
      acc[key].push(room);
      return acc;
    }, {});

  const handleCardClick = (room) => {
    if (bulkMode) {
      onBulkSelect?.(room);
      return;
    }

    const req = {
      a: dayjs(check_time.checkin).format("hh:mm:ss A"),
      b: dayjs(check_time.checkout).format("hh:mm:ss A")
    }


    onRowSelect?.(room);
    navigate('/room/history', { state: { room } })
  };

  const handleRoomStatusToggle = async (checked, room) => {
    try {
      const updatedStatus = checked ? "reserved" : "available";

      setLoadingRoomId(room.id);
      onRoomStatusChange?.(room, updatedStatus);

      // If status is available
      if (updatedStatus !== "reserved") {
        console.log("deleted", room.id, selectedDate);

        const req = await GeneralService.cancelReservation(room.id, selectedDate);
        if (req.status) {
          notify.success(req.message)
        } else {
          notify.error(req.message)
        }
        return;
      }

      // Only runs when reserved
      const currentTime = dayjs().format("hh:mm:ss A");

      const finalPayload = {
        guest_name: "",
        phone: "",
        email: "",
        id_type: "Aadhaar",
        id_number: "",
        address: "",
        guests: 1,
        check_in: selectedDate,
        check_out: selectedDate,
        special_requests: "",
        payment_mode: "Cash",
        room_id: room.id,
        room_data: room,
        check_in_time: dayjs(check_time.checkin).format("hh:mm:ss A"),
        check_out_time: dayjs(check_time.checkout).format("hh:mm:ss A"),
        pricing_summary: {
          rate_per_night: room.price_per_day || 0,
          duration_nights: 1,
          base_total: room.price_per_day || 0,
          extra_guests: 0,
          extra_cost_per_night: 0,
          extra_total: 0,
          total_amount: room.price_per_day || 0,
          paid_amount: 0,
        },
        status: updatedStatus,
        paid_amount: 0,
      };

      console.log("finalPayload", finalPayload);
      console.log("check_time", check_time);

      const res = await fetch(
        ENDPOINTS.BOOKROOM,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(finalPayload),
        });

      const response = await res.json();

      if (response.status) {
        notify.success(`Room marked as ${updatedStatus}`);
      } else {
        notify.error(response.message || "Booking failed");
      }
    } catch (err) {
      console.error("Failed to update room status", err);
      notify.error("Failed to update room status. Please try again.");
    } finally {
      setLoadingRoomId(null);
    }
  };

  const handleMaintenance = async (id, status) => {
    try {
      const request = await RoomServices.editRoom(id, { status: status });
      if (request.status) {
        notify.success(`Room marked as ${status}`);
        // navigate('/book-room')
        getAllRooms()
      } else {
        notify.error(`Failed to mark room as ${status}.`);
      }

    } catch (error) {
      console.error(error);
      notify.error("Something went wrong.");
    }
  }

  const handleCheckOut = async (id, status) => {
    try {
      const request = await RoomServices.checkoutRoom(id, { status: status });
      if (request.status) {
        notify.success(`Room marked as Available`);
        getAllRooms()
      } else {
        notify.error(`Failed to mark room as Available.`);
      }

    } catch (error) {
      console.error(error);
      notify.error("Something went wrong.");
    }
  }

  const getCheckoutStatus = (room) => {
    if (
      room.status?.toLowerCase() !== "occupied" ||
      !room.bl_check_out ||
      !room.bl_check_out_time
    ) {
      return null;
    }

    try {
      const now = new Date();

      // Combine checkout date + checkout time
      const checkoutDateTime = new Date(
        `${room.bl_check_out} ${room.bl_check_out_time}`
      );

      const diffMs = checkoutDateTime.getTime() - now.getTime();

      const absMs = Math.abs(diffMs);
      const hours = Math.floor(absMs / (1000 * 60 * 60));
      const mins = Math.floor((absMs % (1000 * 60 * 60)) / (1000 * 60));

      const formatted =
        hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;

      return {
        overdue: diffMs < 0,
        text:
          diffMs < 0
            ? `+${formatted}`
            : `${formatted} left`,
      };
    } catch (err) {
      return null;
    }
  };

  const renderCard = (room) => {

    // console.log("room@", room);

    const cfg = STATUS_CONFIG[room.status?.toLowerCase()] || DEFAULT_CONFIG;
    const isBulkSelected = selectedRooms.some((r) => r.id === room.id);
    const upcoming = upcomingMap[room.id] || [];
    const checkoutStatus = getCheckoutStatus(room);

    return (
      <div
        key={room.id}
        className={`room-card${isBulkSelected ? " bulk-selected" : ""}`}
        // style={{ "--accent-color": cfg.accent, flex: "1 1 0", minWidth: 0 }}
        style={{
          "--accent-color": cfg.accent,
          flex: "0 0 calc((100% - 80px) / 6)",
          minWidth: 0,
        }}
        onClick={() => {
          if (
            bulkMode &&
            (
              room.status?.toLowerCase() === "maintenance" ||
              room.status?.toLowerCase() === "check_out" ||
              room.status?.toLowerCase() === "occupied" ||
              room.status?.toLowerCase() === "reserved"

            )
          ) {
            return;
          }


          handleCardClick(room);
        }}
      >
        {/* Accent bar */}
        <div style={{ height: 4, background: cfg.accent }} />

        <div
          style={{
            padding: "6px 8px",
            display: "flex",
            flexDirection: "column",
            gap: 5,
          }}
        >
          {/* Room name */}
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#1a1a1a",
              lineHeight: 1.3,
              paddingRight: bulkMode ? 20 : 0,
            }}
          >
            {room.room_name}
          </div>

          {/* Room number */}
          <div style={{ fontSize: 14, color: "#888" }}>#{room.room_number}</div>

          {/* Occupancy + type */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11,
              color: "#555",
            }}
          >
            <UserOutlined style={{ fontSize: 14 }} />
            <span style={{ fontSize: 14 }}>{room.max_occupancy}</span>
            <span style={{ color: "#ccc" }}>·</span>
            <AppstoreOutlined style={{ fontSize: 14 }} />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: 14,
              }}
            >
              {room.room_type}
            </span>
          </div>

        </div>
      </div>
    );
  };

  return (
    <>
      <style>{CARD_STYLES}</style>

      <div>
        {Object.entries(grouped).map(([floor, rooms]) => {
          // Split rooms into rows of COLS
          const rows = [];
          for (let i = 0; i < rooms.length; i += COLS) {
            rows.push(rooms.slice(i, i + COLS));
          }

          return (
            <div key={floor}>
              <Divider orientation="left" orientationMargin={1}>
                <span
                  style={{ fontWeight: 600, fontSize: 16, color: "#1a1a1a" }}
                >
                  Floor: {floor}
                </span>
                <span style={{ marginLeft: 8, fontSize: 12, color: "#888" }}>
                  ({rooms.length} {rooms.length === 1 ? "room" : "rooms"})
                </span>
              </Divider>

              <div style={{ marginBottom: 8 }}>
                {rows.map((rowRooms, rowIdx) => {
                  const isRTL = rowIdx % 2 !== 0;
                  return (
                    <div
                      key={rowIdx}
                      style={{
                        display: "flex",
                        // row-reverse + justify flex-start = items anchor to the RIGHT edge
                        // giving the snake right-aligned behaviour on odd rows
                        flexDirection: isRTL ? "row-reverse" : "row",
                        justifyContent: "flex-start",
                        gap: 16,
                        marginBottom: 16,
                      }}
                    >
                      {rowRooms.map((room) => renderCard(room))}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default HistoryRoomList; 